import React, { useEffect, useState } from 'react';
import { useUser } from '../hooks/useUser';
import useVendorApplications from '../hooks/useVendorApplications';

// Custom styles for the modal
const modalStyles = `
  .bg-gradient-primary {
    background: #667eea;
  }
  .modal-content {
    border-radius: 15px;
    overflow: hidden;
  }
  .modal-header {
    padding: 1.5rem;
    /* color will be set inline for approve/reject */
  }
  .modal-body {
    padding: 2rem;
  }
  .modal-footer {
    padding: 1.5rem;
  }
  .card {
    border-radius: 10px;
  }
  .form-control-plaintext {
    padding: 0.5rem 0;
    color: #495057;
  }
  .btn {
    border-radius: 8px;
    transition: all 0.3s ease;
  }
  .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .modal-header-approve {
    background: #28a745 !important;
    color: #fff !important;
  }
  .modal-header-reject {
    background: #dc3545 !important;
    color: #fff !important;
  }
  .btn-approve {
    background: #28a745 !important;
    color: #fff !important;
    border: none !important;
  }
  .btn-approve:hover {
    background: #218838 !important;
  }
  .btn-reject {
    background: #dc3545 !important;
    color: #fff !important;
    border: none !important;
  }
  .btn-reject:hover {
    background: #c82333 !important;
  }
  .alert-email-notification {
    background: #f8f9fa !important;
    color: #333 !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-radius: 8px;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
  }
  .form-control-admin-notes {
    color: #333 !important;
    background: #f8f9fa !important;
    border: 1px solid #e9ecef !important;
    border-radius: 6px !important;
    font-size: 1rem;
    box-shadow: none !important;
  }
`;

const VendorApplications = () => {
  const { data: user } = useUser();
  const [applications, setApplications] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState({});
  
  const {
    loading,
    error,
    getAllApplications,
    approveApplication,
    rejectApplication,
    deleteApplication,
    clearError
  } = useVendorApplications();

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'employee')) {
      loadApplications();
    }
  }, [user]);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadApplications = async () => {
    const data = await getAllApplications();
    if (data) {
      setApplications(data);
    }
  };

  const handleApprove = async (applicationId) => {
    const application = applications.find(app => app.application_id === applicationId);
    setSelectedApplication(application);
    setSelectedAction('approve');
    setSelectedApplicationId(applicationId);
    setShowModal(true);
  };

  const handleReject = async (applicationId) => {
    const application = applications.find(app => app.application_id === applicationId);
    setSelectedApplication(application);
    setSelectedAction('reject');
    setSelectedApplicationId(applicationId);
    setShowModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedAction || !selectedApplicationId) return;

    setActionLoading(true);
    let result = false;
    
    try {
      if (selectedAction === 'approve') {
        result = await approveApplication(selectedApplicationId, adminNotes);
        if (result) {
          setSuccessMessage(`✅ Application #${selectedApplicationId} has been approved successfully! An email notification has been sent to the vendor.`);
        }
      } else if (selectedAction === 'reject') {
        result = await rejectApplication(selectedApplicationId, adminNotes);
        if (result) {
          setSuccessMessage(`❌ Application #${selectedApplicationId} has been rejected. An email notification has been sent to the vendor.`);
        }
      }

      if (result) {
        setAdminNotes('');
        setShowModal(false);
        setSelectedAction(null);
        setSelectedApplicationId(null);
        setSelectedApplication(null);
        loadApplications(); // Refresh the list
      }
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!actionLoading) {
      setShowModal(false);
      setSelectedAction(null);
      setSelectedApplicationId(null);
      setSelectedApplication(null);
      setAdminNotes('');
    }
  };

  const handleDelete = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      setDeleteLoading(prev => ({ ...prev, [applicationId]: true }));
      
      try {
        const result = await deleteApplication(applicationId);
        if (result) {
          setSuccessMessage(`🗑️ Application #${applicationId} has been deleted successfully.`);
          loadApplications(); // Refresh the list
        }
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setDeleteLoading(prev => ({ ...prev, [applicationId]: false }));
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'badge-warning', text: 'Pending' },
      approved: { class: 'badge-success', text: 'Approved' },
      rejected: { class: 'badge-danger', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || { class: 'badge-secondary', text: status };
    
    return (
      <span className={`badge ${config.class}`}>
        {config.text}
      </span>
    );
  };

  if (!user || (user.role !== 'admin' && user.role !== 'employee')) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <strong>Access Denied:</strong> Only admin or employee can view vendor applications.
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{modalStyles}</style>
      <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Vendor Applications</h2>
              <p className="text-muted mb-0">Manage and review vendor applications</p>
            </div>
            <button 
              className="btn btn-primary" 
              onClick={loadApplications}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Loading...
                </>
              ) : (
                'Refresh'
              )}
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {successMessage}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccessMessage('')}
              ></button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>Error:</strong> {error}
              <button className="btn btn-sm btn-outline-danger ms-2" onClick={clearError}>
                Dismiss
              </button>
            </div>
          )}

          {/* Content */}
          <div className="card">
            <div className="card-body p-0">
              {loading && applications.length === 0 ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading applications...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">No Applications Found</h5>
                  <p className="text-muted">There are no vendor applications to display.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 table-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '60px' }}>ID</th>
                        <th style={{ width: '200px' }}>Actions</th>
                        <th style={{ width: '200px' }}>Name</th>
                        <th style={{ width: '180px' }}>Email</th>
                        <th style={{ width: '140px' }}>Phone</th>
                        <th style={{ width: '120px' }}>Type</th>
                        <th style={{ width: '100px' }}>Country</th>
                        <th style={{ width: '200px' }}>Address</th>
                        <th style={{ width: '100px' }}>Status</th>
                        <th style={{ width: '140px' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map(app => (
                        <tr key={app.application_id}>
                          <td className="align-middle">
                            <strong>#{app.application_id}</strong>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex gap-2">
                              {app.status === 'pending' && (
                                <>
                                  <button 
                                    className="btn btn-success"
                                    onClick={() => handleApprove(app.application_id)}
                                    disabled={loading || actionLoading}
                                    title="Approve Application"
                                    style={{ 
                                      fontSize: '0.8rem', 
                                      padding: '0.4rem 0.8rem',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      border: 'none',
                                      boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!loading && !actionLoading) {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.3)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.2)';
                                    }}
                                  >
                                    {actionLoading && selectedApplicationId === app.application_id ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                        Processing...
                                      </>
                                    ) : (
                                      '✓ Approve'
                                    )}
                                  </button>
                                  <button 
                                    className="btn btn-danger"
                                    onClick={() => handleReject(app.application_id)}
                                    disabled={loading || actionLoading}
                                    title="Reject Application"
                                    style={{ 
                                      fontSize: '0.8rem', 
                                      padding: '0.4rem 0.8rem',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      border: 'none',
                                      boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!loading && !actionLoading) {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(220, 53, 69, 0.3)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 2px 4px rgba(220, 53, 69, 0.2)';
                                    }}
                                  >
                                    {actionLoading && selectedApplicationId === app.application_id ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                        Processing...
                                      </>
                                    ) : (
                                      '✕ Reject'
                                    )}
                                  </button>
                                </>
                              )}
                              <button 
                                className="btn btn-outline-secondary"
                                onClick={() => handleDelete(app.application_id)}
                                disabled={loading || actionLoading || deleteLoading[app.application_id]}
                                title="Delete Application"
                                style={{ 
                                  fontSize: '0.8rem', 
                                  padding: '0.4rem 0.8rem',
                                  borderRadius: '6px',
                                  fontWeight: '500',
                                  border: '1px solid #6c757d',
                                  backgroundColor: 'transparent',
                                  color: '#6c757d',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (!loading && !actionLoading && !deleteLoading[app.application_id]) {
                                    e.target.style.backgroundColor = '#6c757d';
                                    e.target.style.color = 'white';
                                    e.target.style.transform = 'translateY(-1px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#6c757d';
                                  e.target.style.transform = 'translateY(0)';
                                }}
                              >
                                {deleteLoading[app.application_id] ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Deleting...
                                  </>
                                ) : (
                                  '🗑️ Delete'
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="align-middle">
                            <strong>{app.name}</strong>
                          </td>
                          <td className="align-middle">
                            {app.email}
                          </td>
                          <td className="align-middle">
                            {app.phone_no}
                          </td>
                          <td className="align-middle">
                            <span className="badge bg-info">{app.vendor_type}</span>
                          </td>
                          <td className="align-middle">
                            {app.origin || 'N/A'}
                          </td>
                          <td className="align-middle">
                            {app.address || 'N/A'}
                          </td>
                          <td className="align-middle">
                            {getStatusBadge(app.status)}
                          </td>
                          <td className="align-middle">
                            {app.created_at ? new Date(app.created_at).toLocaleString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Notes Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              {/* Modal Header */}
              <div className={`modal-header ${selectedAction === 'approve' ? 'modal-header-approve' : 'modal-header-reject'} border-0`}>
                <h5 className="modal-title fw-bold">
                  <i className={`fas fa-${selectedAction === 'approve' ? 'check-circle' : 'times-circle'} me-2`}></i>
                  {selectedAction === 'approve' ? 'Approve' : 'Reject'} Vendor Application
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={handleCloseModal}
                  disabled={actionLoading}
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body p-4">
                {/* Application Details Card */}
                {selectedApplication && (
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-user me-2"></i>
                        Application Details
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Vendor Name</label>
                            <div className="form-control-plaintext fw-semibold">{selectedApplication.name}</div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Email Address</label>
                            <div className="form-control-plaintext">
                              <i className="fas fa-envelope text-muted me-2"></i>
                              {selectedApplication.email}
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Phone Number</label>
                            <div className="form-control-plaintext">
                              <i className="fas fa-phone text-muted me-2"></i>
                              {selectedApplication.phone_no}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Vendor Type</label>
                            <div className="form-control-plaintext">
                              <span className="badge bg-info me-2">{selectedApplication.vendor_type}</span>
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Country</label>
                            <div className="form-control-plaintext">
                              <i className="fas fa-globe text-muted me-2"></i>
                              {selectedApplication.origin || 'N/A'}
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-bold text-muted small mb-1">Current Status</label>
                            <div className="form-control-plaintext">
                              {getStatusBadge(selectedApplication.status)}
                            </div>
                          </div>
                        </div>
                      </div>
                      {selectedApplication.address && (
                        <div className="mt-3 pt-3 border-top">
                          <label className="form-label fw-bold text-muted small mb-1">Address</label>
                          <div className="form-control-plaintext">
                            <i className="fas fa-map-marker-alt text-muted me-2"></i>
                            {selectedApplication.address}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Admin Notes Section */}
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-bottom">
                    <h6 className="mb-0 fw-bold text-dark">
                      <i className="fas fa-edit me-2"></i>
                      Admin Notes (Optional)
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <textarea
                        id="adminNotes"
                        className="form-control form-control-admin-notes"
                        rows="4"
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add any notes or comments about this decision..."
                        disabled={actionLoading}
                        style={{ resize: 'none' }}
                      ></textarea>
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        These notes will be included in the email notification to the vendor.
                      </small>
                    </div>
                  </div>
                </div>

                {/* Action Information */}
                <div className="alert alert-email-notification">
                  <div className="d-flex align-items-start">
                    <i className={`fas fa-${selectedAction === 'approve' ? 'check-circle' : 'exclamation-triangle'} me-3 mt-1`}></i>
                    <div>
                      <strong>Email Notification:</strong> An email will be automatically sent to the vendor with the decision and any notes you provide.
                      {selectedAction === 'approve' && (
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Note:</strong> A 6-digit password will be automatically generated and included in the approval email.
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer bg-light border-0">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary px-4" 
                  onClick={handleCloseModal}
                  disabled={actionLoading}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className={`btn ${selectedAction === 'approve' ? 'btn-approve' : 'btn-reject'} px-4 fw-bold`}
                  onClick={handleConfirmAction}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className={`fas fa-${selectedAction === 'approve' ? 'check' : 'times'} me-2`}></i>
                      {selectedAction === 'approve' ? 'Approve Application' : 'Reject Application'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
    </>
  );
};

export default VendorApplications; 